import { RiMailSendLine } from '@remixicon/react';
import { getRoleDisplayName } from '@shared/constants/clerk';
import { useMemo } from 'react';
import { useAuth, useIsOrgAdmin } from 'src/auth';
import PlaceCell from 'src/components/atoms/grid/PlaceCell';
import { AgText } from 'src/components/ui';
import { GridColObj } from 'src/modules/grids';
import columnActions from 'src/utils/grid/column/columnActions';
import { useUpdateMembership } from '../hooks/useUpdateMembership';
import { PeopleRow } from './usePeopleRows';

export default function usePeopleColumns() {
  const isAdmin = useIsOrgAdmin();
  const { currentUser } = useAuth();
  const { update: updateMembership } = useUpdateMembership();

  return useMemo<GridColObj<PeopleRow>>(
    () => ({
      name: {
        headerName: 'Name',
        flex: 1,
        renderCell: ({ value, row }) => (
          <div className="flex items-center gap-4">
            {row.imageUrl && (
              <img
                src={row.imageUrl}
                alt={value}
                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
              />
            )}
            {row.type === 'invitation' && (
              <div
                className="flex items-center justify-center"
                style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#e0e0e0' }}
              >
                <RiMailSendLine size={20} className="text-brand-7" />
              </div>
            )}
            <AgText
              size={row.type === 'invitation' ? '12' : '14'}
              className={row.type === 'invitation' ? 'uppercase tracking-wider' : ''}
            >
              {value}
            </AgText>
          </div>
        ),
      },
      email: { headerName: 'Email', flex: 1, renderCell: ({ value }) => <AgText size="14">{value}</AgText> },
      subsidiary: {
        headerName: 'Subsidiary',
        flex: 1,
        valueGetter: (_value, row) => row.subsidiaryId,
        renderCell: (params) =>
          params.row.type === 'member' ? (
            <PlaceCell
              addressRoles={['SUBSIDIARY', 'PRIMARY_BUSINESS']}
              businessEntityId={null}
              strict={true}
              onChange={(e, option) => {
                // Handle subsidiary change. A Clerk member with no platform membership row has nothing to update
                // (GraphQL rejected the missing id before).
                if (params.row.membershipId === undefined) return;
                updateMembership({ id: params.row.membershipId, input: { subsidiaryId: option?.id ?? null } });
              }}
              {...params}
            />
          ) : null,
      },
      role: {
        headerName: 'Role',
        flex: 1,
        renderCell: ({ value }) => <AgText size="14">{getRoleDisplayName(value)}</AgText>,
      },
      actions: {
        type: 'actions',
        headerName: '',
        maxWidth: 80,
        minWidth: 80,
        align: 'center',
        getActions: columnActions(({ row }) => {
          const actions: Record<string, unknown> = {};

          if (row.type === 'invitation' && isAdmin) {
            actions.deleteRow = ({ row }) => {
              row.remove();
            };
          }

          if (row.type === 'member' && isAdmin && row.id !== currentUser.id) {
            actions.deleteRow = ({ row }) => {
              row.remove();
            };
          }

          return actions;
        }),
      },
    }),
    [isAdmin, currentUser.id, updateMembership]
  );
}
